/**
 * Reading and keeping the referral code from a referral link (P25028-80).
 *
 * A referral link is the normal signup address plus a code:
 * `https://<site>/signup?ref=AB12CD`.
 *
 * The code is copied into session storage as soon as the visitor lands, because
 * the address bar loses it in two ordinary cases:
 *
 *  1. They click "Log in", then click back to "Sign up" — those links carry no
 *     query string.
 *  2. They choose "Continue with Google" / "Continue with Facebook", which
 *     leaves the site and comes back to `/confirm`.
 *
 * Session storage, not local storage: the code should last for the visit and no
 * longer, so a shared computer does not attribute a much later signup to
 * whoever happened to use it first.
 */
import { parse } from './urlHelpers';

// The query parameter on a referral link.
export const REFERRAL_CODE_PARAM = 'ref';

const STORAGE_KEY = 'causeeReferralCode';

// Codes we generate are 6 characters of capitals and digits. The range is kept
// slightly wider than that so a future change to the code format does not
// silently start dropping valid links. Anything else is not a code we issued,
// so it is ignored rather than stored and sent on.
const CODE_PATTERN = /^[A-Z0-9]{4,12}$/;

// Session storage throws in a few real browsers (Safari private mode, storage
// disabled by policy) and does not exist during server-side rendering.
const getStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
};

/** The code in a usable shape, or null if it is not one we could have issued. */
export const normalizeReferralCode = code => {
  if (typeof code !== 'string') {
    return null;
  }
  const normalized = code.trim().toUpperCase();
  return CODE_PATTERN.test(normalized) ? normalized : null;
};

/**
 * Save the code from a `?ref=` link, if the given search string carries one.
 *
 * A link without a code leaves an already-saved one alone: the visitor may have
 * arrived on a referral link and then navigated within the auth pages.
 */
export const rememberReferralCode = search => {
  const { [REFERRAL_CODE_PARAM]: code } = parse(search) || {};
  const normalized = normalizeReferralCode(code);
  if (!normalized) {
    return null;
  }

  const storage = getStorage();
  try {
    storage?.setItem(STORAGE_KEY, normalized);
  } catch (e) {
    // Storage is unavailable — the code still works for this page load, since
    // readReferralCode falls back to the address bar.
  }
  return normalized;
};

/**
 * The code for this visit: whatever is in the address bar, otherwise whatever
 * we saved earlier. Returns null when the visitor did not come from a link.
 */
export const readReferralCode = search => {
  const fromSearch = normalizeReferralCode(
    (parse(search) || {})[REFERRAL_CODE_PARAM]
  );
  if (fromSearch) {
    return fromSearch;
  }

  const storage = getStorage();
  try {
    return normalizeReferralCode(storage?.getItem(STORAGE_KEY));
  } catch (e) {
    return null;
  }
};

/** Forget the saved code, once it has been recorded against the new account. */
export const clearReferralCode = () => {
  const storage = getStorage();
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch (e) {
    // Nothing to do — a stale code only lasts for this visit anyway.
  }
};
